
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, AlertTriangle, Wrench, CheckCircle, Clock, Users, Shield, Database, MessageSquare, Square, Circle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import FeedbackModal from '@/components/FeedbackModal';

const RoadmapPage = () => {
  const [feedbackModal, setFeedbackModal] = useState({
    isOpen: false,
    itemId: null,
    itemTitle: ''
  });

  const { toast } = useToast();

  const handleFeedbackClick = (itemId, itemTitle) => {
    setFeedbackModal({
      isOpen: true,
      itemId,
      itemTitle
    });
  };

  const handleFeedbackSubmit = (feedbackData) => {
    console.log('Feedback received:', feedbackData);
    
    toast({
      title: "Feedback verzonden!",
      description: "Bedankt voor je waardevolle feedback. We nemen dit mee in onze ontwikkeling.",
      duration: 4000,
    });
  };

  const feedbackButton = (itemId, itemTitle) => (
    <button 
      onClick={() => handleFeedbackClick(itemId, itemTitle)}
      className="flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 bg-white/50 hover:bg-white/70 hover:scale-105 text-cm-gray-dark"
    >
      <MessageSquare className="w-4 h-4 mr-2" />
      Geef feedback
    </button>
  );

  const fadeIn = (delay = 0, y = 20) => ({
    hidden: { opacity: 0, y: y, scale: 0.98 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.7, delay, ease: [0.6, -0.05, 0.01, 0.99] } },
  });

  return (
    <div className="container mx-auto px-4 section-padding overflow-hidden">
      {/* Header */}
      <motion.div
        initial="hidden"
        animate="visible"
        className="text-center mb-16"
      >
        <motion.h1
          variants={fadeIn(0, 30)}
          className="text-4xl md:text-5xl font-sans font-medium mb-4"
        >
          <span className="text-gradient-pink-blue">Classbuddy Roadmap</span>
        </motion.h1>
        <motion.p
          variants={fadeIn(0.1, 20)}
          className="text-2xl md:text-3xl font-sans font-medium text-cm-blue-dark mb-6"
        >
          Samen groeien, stap voor stap
        </motion.p>
        <motion.p
          variants={fadeIn(0.2, 20)}
          className="page-subtitle"
        >
          Op deze pagina zie je wat al gelanceerd is, wat eraan komt en waar we op dit moment aan werken. Zo blijven scholen, gemeentes en ouders altijd op de hoogte. Transparant, veilig en met het kind centraal.
        </motion.p>
      </motion.div>

      {/* Al gelanceerd */}
      <motion.section
        variants={fadeIn(0.2, 30)}
        initial="hidden"
        animate="visible"
        className="mb-16"
      >
        <div className="flex items-center mb-8">
          <Square className="w-6 h-6 text-cm-green fill-cm-green mr-3" />
          <h2 className="text-3xl font-sans font-medium text-cm-blue-dark">Al gelanceerd</h2>
        </div>

        <div className="grid gap-6">
          {/* Buddy Nouri */}
          <div className="glassmorphism-card p-6 rounded-2xl border-l-4 border-cm-green">
            <div className="flex items-start mb-3">
              <Square className="w-5 h-5 text-cm-green fill-cm-green mr-3 mt-0.5 flex-shrink-0" />
              <div className="flex-grow">
                <h3 className="text-xl font-medium text-cm-blue-dark mb-2">Buddy Nouri – Vriendschap & Groepsvorming</h3>
                <p className="text-sm text-cm-gray mb-2">Live sinds: juni 2025</p>
                <p className="text-cm-gray">Kinderen praten met Nouri over vrienden maken, erbij horen en samen spelen.</p>
              </div>
            </div>
            <div className="mt-3">
              {feedbackButton('nouri', 'Buddy Nouri – Vriendschap & Groepsvorming')}
            </div>
          </div>

          {/* Buddy Luma */}
          <div className="glassmorphism-card p-6 rounded-2xl border-l-4 border-cm-green">
            <div className="flex items-start mb-3">
              <Square className="w-5 h-5 text-cm-green fill-cm-green mr-3 mt-0.5 flex-shrink-0" />
              <div className="flex-grow">
                <h3 className="text-xl font-medium text-cm-blue-dark mb-2">Buddy Luma – Angst & Spanning</h3>
                <p className="text-sm text-cm-gray mb-2">Live sinds: juni 2025</p>
                <p className="text-cm-gray">Luma helpt bij spanning, nachtmerries, piekergedachten of stress op school.</p>
              </div>
            </div>
            <div className="mt-3">
              {feedbackButton('luma', 'Buddy Luma – Angst & Spanning')}
            </div>
          </div>

          {/* Buddy Appie */}
          <div className="glassmorphism-card p-6 rounded-2xl border-l-4 border-cm-green">
            <div className="flex items-start mb-3">
              <Square className="w-5 h-5 text-cm-green fill-cm-green mr-3 mt-0.5 flex-shrink-0" />
              <div className="flex-grow">
                <h3 className="text-xl font-medium text-cm-blue-dark mb-2">Buddy Appie – Pesten & Grenzen Stellen</h3>
                <p className="text-sm text-cm-gray mb-2">Live sinds: juni 2025</p>
                <p className="text-cm-gray">Appie praat over gepest worden, grenzen aangeven en zelfrespect.</p>
              </div>
            </div>
            <div className="mt-3">
              {feedbackButton('appie', 'Buddy Appie – Pesten & Grenzen Stellen')}
            </div>
          </div>

          {/* Dashboard */}
          <div className="glassmorphism-card p-6 rounded-2xl border-l-4 border-cm-green">
            <div className="flex items-start mb-3">
              <Square className="w-5 h-5 text-cm-green fill-cm-green mr-3 mt-0.5 flex-shrink-0" />
              <div className="flex-grow">
                <h3 className="text-xl font-medium text-cm-blue-dark mb-2">Dashboard voor scholen</h3>
                <p className="text-sm text-cm-gray mb-2">Live sinds: juni 2025</p>
                <p className="text-cm-gray">IB'ers en leerkrachten kunnen zien hoeveel gesprekken er zijn, per buddy of per thema. Geanonimiseerd.</p>
              </div>
            </div>
            <div className="mt-3">
              {feedbackButton('dashboard', 'Dashboard voor scholen')}
            </div>
          </div>

          {/* Veilige inlogstructuur */}
          <div className="glassmorphism-card p-6 rounded-2xl border-l-4 border-cm-green">
            <div className="flex items-start mb-3">
              <Square className="w-5 h-5 text-cm-green fill-cm-green mr-3 mt-0.5 flex-shrink-0" />
              <div className="flex-grow">
                <h3 className="text-xl font-bold text-cm-blue-dark mb-2">Veilige inlogstructuur (AVG-proof)</h3>
                <p className="text-cm-gray">Gebruikers loggen in via schoolcode, zonder persoonsgegevens. Veilig en kindvriendelijk.</p>
              </div>
            </div>
            <div className="mt-3">
              {feedbackButton('login', 'Veilige inlogstructuur (AVG-proof)')}
            </div>
          </div>
        </div>
      </motion.section>

      {/* Binnenkort beschikbaar */}
      <motion.section
        variants={fadeIn(0.3, 30)}
        initial="hidden"
        animate="visible"
        className="mb-16"
      >
        <div className="flex items-center mb-8">
          <Square className="w-6 h-6 text-cm-blue fill-cm-blue mr-3" />
          <h2 className="text-3xl font-sans font-bold text-cm-blue-dark">Binnenkort beschikbaar</h2>
        </div>

        <div className="grid gap-6">
          {/* Buddy Rafi */}
          <div className="glassmorphism-card p-6 rounded-2xl border-l-4 border-cm-blue">
            <div className="flex items-start mb-3">
              <Square className="w-5 h-5 text-cm-blue fill-cm-blue mr-3 mt-0.5 flex-shrink-0" />
              <div className="flex-grow">
                <h3 className="text-xl font-bold text-cm-blue-dark mb-2">Buddy Rafi – Zelfvertrouwen & Keuzes maken</h3>
                <p className="text-sm text-cm-blue font-semibold mb-2">Verwacht: juli 2025</p>
                <p className="text-cm-gray">Kinderen praten met Rafi over twijfels, faalangst en moedige keuzes maken.</p>
              </div>
            </div>
            <div className="mt-3">
              {feedbackButton('rafi', 'Buddy Rafi – Zelfvertrouwen & Keuzes maken')}
            </div>
          </div>

          {/* Buddy Zena */}
          <div className="glassmorphism-card p-6 rounded-2xl border-l-4 border-cm-blue">
            <div className="flex items-start mb-3">
              <Square className="w-5 h-5 text-cm-blue fill-cm-blue mr-3 mt-0.5 flex-shrink-0" />
              <div className="flex-grow">
                <h3 className="text-xl font-bold text-cm-blue-dark mb-2">Buddy Zena – Omgaan met Gevoelens</h3>
                <p className="text-sm text-cm-blue font-semibold mb-2">Verwacht: juli 2025</p>
                <p className="text-cm-gray">Zena helpt gevoelens begrijpen, benoemen en ermee omgaan op school en thuis.</p>
              </div>
            </div>
            <div className="mt-3">
              {feedbackButton('zena', 'Buddy Zena – Omgaan met Gevoelens')}
            </div>
          </div>

          {/* Buddy Chloe */}
          <div className="glassmorphism-card p-6 rounded-2xl border-l-4 border-cm-blue">
            <div className="flex items-start mb-3">
              <Square className="w-5 h-5 text-cm-blue fill-cm-blue mr-3 mt-0.5 flex-shrink-0" />
              <div className="flex-grow">
                <h3 className="text-xl font-bold text-cm-blue-dark mb-2">Buddy Chloe – Veranderingen Thuis</h3>
                <p className="text-sm text-cm-blue font-semibold mb-2">Verwacht: juli 2025</p>
                <p className="text-cm-gray">Chloe ondersteunt bij scheiding, verhuizing of spanningen in de thuissituatie.</p>
              </div>
            </div>
            <div className="mt-3">
              {feedbackButton('chloe', 'Buddy Chloe – Veranderingen Thuis')}
            </div>
          </div>

          {/* Chatbot */}
          <div className="glassmorphism-card p-6 rounded-2xl border-l-4 border-cm-blue">
            <div className="flex items-start mb-3">
              <Square className="w-5 h-5 text-cm-blue fill-cm-blue mr-3 mt-0.5 flex-shrink-0" />
              <div className="flex-grow">
                <h3 className="text-xl font-bold text-cm-blue-dark mb-2">Chatbot voor scholen & gemeentes (op homepage)</h3>
                <p className="text-sm text-cm-blue font-semibold mb-2">Verwacht: augustus 2025</p>
                <p className="text-cm-gray">AI-chatbot die antwoord geeft op veelgestelde vragen over buddy's, AVG, privacy, implementatie en kosten.</p>
              </div>
            </div>
            <div className="mt-3">
              {feedbackButton('chatbot', 'Chatbot voor scholen & gemeentes')}
            </div>
          </div>
        </div>
      </motion.section>

      {/* Urgente functie */}
      <motion.section
        variants={fadeIn(0.4, 30)}
        initial="hidden"
        animate="visible"
        className="mb-16"
      >
        <div className="flex items-center mb-8">
          <Circle className="w-6 h-6 text-red-500 fill-red-500 mr-3" />
          <h2 className="text-3xl font-sans font-bold text-cm-blue-dark">Urgente functie (veiligheid & signalering)</h2>
        </div>

        <div className="bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-red-500 p-8 rounded-2xl shadow-lg">
          <div className="flex items-start mb-4">
            <Circle className="w-6 h-6 text-red-500 fill-red-500 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-2xl font-bold text-red-700 mb-4">Rode Signaalfunctie – Interne melding bij ernstige gesprekken</h3>
              <p className="text-sm text-red-600 font-semibold mb-4">Verwacht: bij lancering</p>
            </div>
          </div>
          
          <div className="mb-6">
            <p className="text-gray-700 mb-4">Wanneer een kind praat over:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-1 mb-6">
              <li>suïcidale gedachten</li>
              <li>kindermishandeling en huiselijk geweld</li>
              <li>ernstige pesterijen</li>
            </ul>
            <p className="text-gray-700 mb-4">…geeft Classbuddy een rood intern signaal in het dashboard:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-1 mb-6">
              <li>buddy-icoon licht rood op</li>
              <li>alert verschijnt bij het gesprek</li>
              <li>alert zichtbaar voor IB'er of zorgcoördinator</li>
            </ul>
            <p className="text-gray-700 mb-4 font-semibold">Belangrijk: gesprekken kun je niet terug lezen, alleen signalering is zichtbaar.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mt-6">
            <div className="flex items-center bg-white/70 p-3 rounded-lg">
              <Shield className="w-5 h-5 text-green-600 mr-2" />
              <span className="text-sm font-medium">Alleen zichtbaar voor geautoriseerde medewerkers</span>
            </div>
            <div className="flex items-center bg-white/70 p-3 rounded-lg">
              <Shield className="w-5 h-5 text-blue-600 mr-2" />
              <span className="text-sm font-medium">Volledig AVG-proof | Geen melding aan het kind</span>
            </div>
            <div className="flex items-center bg-white/70 p-3 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-orange-600 mr-2" />
              <span className="text-sm font-medium">Doel: vroegsignalering, bescherming en actie</span>
            </div>
          </div>
          <div className="mt-3">
            {feedbackButton('red-signal', 'Rode Signaalfunctie – Interne melding bij ernstige gesprekken')}
          </div>
        </div>
      </motion.section>

      {/* In ontwikkeling */}
      <motion.section
        variants={fadeIn(0.5, 30)}
        initial="hidden"
        animate="visible"
        className="mb-16"
      >
        <div className="flex items-center mb-8">
          <Square className="w-6 h-6 text-orange-500 fill-orange-500 mr-3" />
          <h2 className="text-3xl font-sans font-bold text-cm-blue-dark">In ontwikkeling</h2>
        </div>

        <div className="grid gap-6">
          <div className="glassmorphism-card p-6 rounded-2xl border-l-4 border-orange-400">
            <div className="flex items-start mb-3">
              <Square className="w-5 h-5 text-orange-500 fill-orange-500 mr-3 mt-0.5 flex-shrink-0" />
              <div className="flex-grow">
                <h3 className="text-xl font-bold text-cm-blue-dark mb-2">Rapportagefunctie (geanonimiseerd)</h3>
                <p className="text-cm-gray">Exporteer maandelijkse inzichten per klas of buddy-thema. Ideaal voor IB'ers en MT's.</p>
              </div>
            </div>
            <div className="mt-3">
              {feedbackButton('reporting', 'Rapportagefunctie (geanonimiseerd)')}
            </div>
          </div>

          <div className="glassmorphism-card p-6 rounded-2xl border-l-4 border-orange-400">
            <div className="flex items-start mb-3">
              <Square className="w-5 h-5 text-orange-500 fill-orange-500 mr-3 mt-0.5 flex-shrink-0" />
              <div className="flex-grow">
                <h3 className="text-xl font-bold text-cm-blue-dark mb-2">Feedbackknop in dashboard</h3>
                <p className="text-cm-gray">Leerkrachten kunnen direct opmerkingen of wensen doorgeven over buddy-gesprekken of AI-antwoorden.</p>
              </div>
            </div>
            <div className="mt-3">
              {feedbackButton('dashboard-feedback', 'Feedbackknop in dashboard')}
            </div>
          </div>

          <div className="glassmorphism-card p-6 rounded-2xl border-l-4 border-orange-400">
            <div className="flex items-start mb-3">
              <Square className="w-5 h-5 text-orange-500 fill-orange-500 mr-3 mt-0.5 flex-shrink-0" />
              <div className="flex-grow">
                <h3 className="text-xl font-bold text-cm-blue-dark mb-2">Trainingsintegratie</h3>
                <p className="text-cm-gray">Koppel buddy-thema's aan live trainingen van Classbuddy Academy. Direct te boeken via je dashboard.</p>
              </div>  
            </div>
            <div className="mt-3">
              {feedbackButton('training-integration', 'Trainingsintegratie')}
            </div>
          </div>

          <div className="glassmorphism-card p-6 rounded-2xl border-l-4 border-orange-400">
            <div className="flex items-start mb-3">
              <Square className="w-5 h-5 text-orange-500 fill-orange-500 mr-3 mt-0.5 flex-shrink-0" />
              <div className="flex-grow">
                <h3 className="text-xl font-bold text-cm-blue-dark mb-2">Mobiele optimalisatie</h3>
                <p className="text-cm-gray">Kindvriendelijke knoppen en betere weergave voor iPads, Chromebooks en telefoons.</p>
              </div>
            </div>
            <div className="mt-3">
              {feedbackButton('mobile-optimization', 'Mobiele optimalisatie')}
            </div>
          </div>
        </div>
      </motion.section>

      {/* Interactie & feedback */}
      <motion.section
        variants={fadeIn(0.6, 30)}
        initial="hidden"
        animate="visible"
        className="mb-16"
      >
        <div className="flex items-center mb-8">
          <MessageSquare className="w-8 h-8 text-cm-pink mr-3" />
          <h2 className="text-3xl font-sans font-bold text-cm-blue-dark">Interactie & feedback</h2>
        </div>

        <div className="glassmorphism-card p-8 rounded-2xl">
          <div className="grid md:grid-cols-2 gap-6 text-center">
            <div>
              <MessageSquare className="w-12 h-12 text-cm-blue mx-auto mb-3" />
              <p className="text-cm-gray">💬 Geef gedetailleerde feedback op elke functie</p>
            </div>
            <div>
              <Users className="w-12 h-12 text-cm-green mx-auto mb-3" />
              <p className="text-cm-gray">📌 Deel je ervaringen en suggesties met het Classbuddy team</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Call-to-action */}
      <motion.section
        variants={fadeIn(0.7, 30)}
        initial="hidden"
        animate="visible"
        className="mb-16"
      >
        <div className="bg-gradient-to-r from-cm-blue-light/70 via-cm-yellow-light/70 to-cm-pink-light/70 rounded-3xl p-10 text-center shadow-2xl">
          <h2 className="text-4xl font-sans font-bold text-cm-blue-dark mb-6">Call-to-action</h2>
          <p className="text-xl text-cm-gray mb-8 max-w-2xl mx-auto leading-relaxed">
            Wil je meedenken, testen of samenwerken met Classbuddy?<br />
            Vraag vrijblijvend een demo aan of bekijk onze trainingen.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-cm-blue hover:bg-cm-blue-dark text-white font-bold text-lg px-8 py-6 rounded-xl shadow-lg transform hover:scale-105 transition-all duration-300">
              <Link to="/contact">
                Vraag een demo aan
              </Link>
            </Button>
            <Button asChild size="lg" className="bg-cm-green hover:bg-cm-green-dark text-white font-bold text-lg px-8 py-6 rounded-xl shadow-lg transform hover:scale-105 transition-all duration-300">
              <Link to="/trainingen">
                Bekijk trainingen
              </Link>
            </Button>
          </div>
        </div>
      </motion.section>

      {/* AVG Disclaimer */}
      <motion.section
        variants={fadeIn(0.8, 30)}
        initial="hidden"
        animate="visible"
        className="text-center"
      >
        <div className="bg-gray-50 p-6 rounded-xl">
          <h3 className="text-lg font-semibold text-cm-gray-dark mb-3">Vertrouwelijkheid & AVG</h3>
          <p className="text-sm text-cm-gray leading-relaxed max-w-4xl mx-auto">
            Classbuddy herkent ernstige signalen op basis van taalpatronen. Er worden geen diagnoses gesteld. Alleen bevoegde schoolmedewerkers kunnen signaleringen zien. Kinderen krijgen nooit directe feedback op deze signalen. Alles verloopt volgens de AVG en met maximale zorg voor veiligheid.
          </p>
        </div>
      </motion.section>

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={feedbackModal.isOpen}
        onClose={() => setFeedbackModal({ isOpen: false, itemId: null, itemTitle: '' })}
        itemTitle={feedbackModal.itemTitle}
        onSubmit={handleFeedbackSubmit}
      />
    </div>
  );
};

export default RoadmapPage;
